import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Calendar, Clock, CheckCircle, XCircle, AlertCircle, Gift, 
  Stethoscope, Award, ChevronRight, RefreshCw, Copy, QrCode,
  CalendarPlus, History, Sparkles, Lock, Unlock, TrendingUp,
  CreditCard, Store
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800',
  depleted: 'bg-red-100 text-red-800',
  expiring: 'bg-yellow-100 text-yellow-800'
};

const CATEGORY_ICONS = {
  consultation: Stethoscope,
  checkup: CheckCircle,
  lab: Stethoscope,
  specialist: Award,
  general: Calendar,
  other: Calendar
};

const MembershipCenter = ({ member, onScheduleService }) => {
  const { t } = useTranslation();
  const [fulfillmentData, setFulfillmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedService, setSelectedService] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationCode, setValidationCode] = useState(null);

  const loadFulfillmentData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fulfillment/my-membership`);
      setFulfillmentData(response.data);
    } catch (error) {
      console.error('Failed to load fulfillment data:', error);
      toast.error('Failed to load membership data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFulfillmentData();
  }, [loadFulfillmentData]);

  const handleScheduleService = (service) => {
    if (onScheduleService) {
      onScheduleService(service);
    } else {
      setSelectedService(service);
      toast.info(t('membershipCenter.navigateToAppointments'));
    }
  };

  const handleShowValidationCode = async (benefit) => {
    try {
      const response = await axios.get(`${API}/fulfillment/my-validation-code/${benefit.benefit_id}`);
      setValidationCode(response.data);
      setShowValidationDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get validation code');
    }
  };

  const copyValidationCode = () => {
    if (validationCode?.validation_code) {
      navigator.clipboard.writeText(validationCode.validation_code);
      toast.success(t('membershipCenter.codeCopied'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!fulfillmentData || !fulfillmentData.plan_id) {
    return (
      <Card className="border-2 border-dashed">
        <CardContent className="py-12 text-center">
          <Gift className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('noMembership.title')}</h3>
          <p className="text-gray-500 mb-4">{t('noMembership.description')}</p>
          <Button className="bg-teal-600 hover:bg-teal-700">
            {t('noMembership.browsePlans')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { services, benefits, service_history, upcoming_appointments } = fulfillmentData;
  const planBenefits = fulfillmentData.plan_benefits || [];
  const planDescription = fulfillmentData.plan_description || '';
  const availableServices = services.filter(s => s.status === 'available' || s.status === 'expiring');
  const depletedServices = services.filter(s => s.status === 'depleted');
  const unlockedBenefits = benefits.filter(b => b.is_unlocked);
  const lockedBenefits = benefits.filter(b => !b.is_unlocked);

  return (
    <div className="space-y-6">
      {/* Header Card - Membership Overview */}
      <Card className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{fulfillmentData.plan_name}</h2>
              <p className="text-teal-100">
                {t('membership.memberSinceMonths', { count: fulfillmentData.membership_months })}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-white/20 text-white border-0">
                  {fulfillmentData.membership_status === 'active' ? t('membership.active') : fulfillmentData.membership_status}
                </Badge>
                {fulfillmentData.payment_gateway && (
                  <Badge className="bg-white/10 text-white border border-white/30 flex items-center gap-1" data-testid="payment-gateway-badge">
                    {fulfillmentData.payment_gateway === 'clover' ? (
                      <Store className="h-3 w-3" />
                    ) : (
                      <CreditCard className="h-3 w-3" />
                    )}
                    <span className="capitalize">{fulfillmentData.payment_gateway}</span>
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold">{fulfillmentData.overall_utilization}%</div>
              <p className="text-sm text-teal-100">{t('membershipCenter.overallUtilization')}</p>
            </div>
          </div>
          
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span>{t('membershipCenter.servicesUsed')}</span>
              <span>{services.reduce((acc, s) => acc + s.quantity_used, 0)} / {services.reduce((acc, s) => acc + (s.quantity_allowed > 0 ? s.quantity_allowed : 0), 0) || '∞'}</span>
            </div>
            <Progress value={fulfillmentData.overall_utilization} className="h-3 bg-white/20" />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/20">
            <div className="text-center">
              <div className="text-2xl font-bold">{availableServices.length}</div>
              <div className="text-xs text-teal-100">{t('membershipCenter.availableServices')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{upcoming_appointments.length}</div>
              <div className="text-xs text-teal-100">{t('membershipCenter.upcomingAppointments')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{unlockedBenefits.length}</div>
              <div className="text-xs text-teal-100">{t('membership.activeBenefits')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="overview">
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('membershipCenter.overview')}
          </TabsTrigger>
          <TabsTrigger value="services">
            <Stethoscope className="h-4 w-4 mr-2" />
            {t('membershipCenter.services')}
          </TabsTrigger>
          <TabsTrigger value="benefits">
            <Gift className="h-4 w-4 mr-2" />
            {t('membershipCenter.benefits')}
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            {t('membershipCenter.history')}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Plan Benefits - Text descriptions from the plan */}
          {planBenefits.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-teal-600" />
                  {t('membershipCenter.yourPlanIncludes', 'Your Plan Includes')}
                </CardTitle>
                {planDescription && (
                  <CardDescription>{planDescription}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {planBenefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Appointments */}
          {upcoming_appointments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-600" />
                  {t('membershipCenter.upcomingAppointments')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcoming_appointments.slice(0, 3).map((apt) => (
                    <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium">{apt.service_name}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(apt.appointment_date).toLocaleDateString()} {t('common.at')} {new Date(apt.appointment_date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </p>
                        </div>
                      </div>
                      <Badge className={apt.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {apt.status === 'confirmed' ? t('appointments.confirmed') : t('appointments.scheduled')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Services Needing Attention */}
          {services.filter(s => s.status === 'expiring' || (s.quantity_allowed > 0 && s.quantity_used === 0)).length > 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="h-5 w-5" />
                  {t('membershipCenter.servicesToSchedule')}
                </CardTitle>
                <CardDescription className="text-yellow-700">
                  {t('membershipCenter.dontMissOut')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {services.filter(s => s.status === 'expiring' || (s.quantity_allowed > 0 && s.quantity_used === 0)).slice(0, 3).map((service) => (
                    <div key={service.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-gray-500">
                          {service.quantity_remaining} {t('common.of')} {service.quantity_allowed} {t('membershipCenter.remaining')}
                        </p>
                      </div>
                      <Button size="sm" onClick={() => handleScheduleService(service)} className="bg-yellow-600 hover:bg-yellow-700">
                        <CalendarPlus className="h-4 w-4 mr-1" />
                        {t('membershipCenter.schedule')}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('services')}>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarPlus className="h-6 w-6 text-teal-600" />
                </div>
                <h4 className="font-semibold">{t('membershipCenter.scheduleService')}</h4>
                <p className="text-sm text-gray-500">{t('membershipCenter.bookNextAppointment')}</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('benefits')}>
              <CardContent className="pt-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <Gift className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="font-semibold">{t('membershipCenter.viewBenefits')}</h4>
                <p className="text-sm text-gray-500">{t('membershipCenter.checkDiscounts')}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t('membershipCenter.includedServices')}</h3>
            <Badge variant="outline">{services.length} {t('common.services')}</Badge>
          </div>

          {/* Available Services */}
          <div className="space-y-3">
            {availableServices.map((service) => {
              const CategoryIcon = CATEGORY_ICONS[service.category] || Calendar;
              const isExpiring = service.status === 'expiring';
              
              return (
                <Card key={service.id} className={isExpiring ? 'border-yellow-300' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isExpiring ? 'bg-yellow-100' : 'bg-teal-100'}`}>
                          <CategoryIcon className={`h-5 w-5 ${isExpiring ? 'text-yellow-600' : 'text-teal-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{service.name}</h4>
                            <Badge className={STATUS_COLORS[service.status]}>
                              {service.status === 'expiring' ? t('serviceStatus.expiring') : t('serviceStatus.available')}
                            </Badge>
                          </div>
                          {service.description && (
                            <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                            {service.quantity_allowed > 0 ? (
                              <span>
                                <strong>{service.quantity_used}</strong>/{service.quantity_allowed} {t('membershipCenter.used')}
                              </span>
                            ) : (
                              <span className="text-green-600">{t('membershipCenter.unlimited')}</span>
                            )}
                            {service.quantity_scheduled > 0 && (
                              <span className="text-blue-600">
                                {service.quantity_scheduled} {t('membershipCenter.scheduled')}
                              </span>
                            )}
                            <span className="text-gray-400">|</span>
                            <span>{t('membershipCenter.per')} {service.period_type}</span>
                          </div>
                          {service.quantity_allowed > 0 && (
                            <Progress 
                              value={(service.quantity_used / service.quantity_allowed) * 100} 
                              className="h-2 mt-2 w-48"
                            />
                          )}
                        </div>
                      </div>
                      {service.can_schedule && (
                        <Button 
                          onClick={() => handleScheduleService(service)}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          <CalendarPlus className="h-4 w-4 mr-2" />
                          {t('membershipCenter.schedule')}
                        </Button>
                      )}
                    </div>
                    {service.last_used_date && (
                      <p className="text-xs text-gray-400 mt-3">
                        {t('membershipCenter.lastUsed')}: {new Date(service.last_used_date).toLocaleDateString()}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Depleted Services */}
          {depletedServices.length > 0 && (
            <>
              <h4 className="text-md font-medium text-gray-600 mt-6">{t('membershipCenter.fullyUsed')}</h4>
              <div className="space-y-2">
                {depletedServices.map((service) => (
                  <Card key={service.id} className="bg-gray-50 border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <XCircle className="h-5 w-5 text-gray-400" />
                          <div>
                            <h4 className="font-medium text-gray-600">{service.name}</h4>
                            <p className="text-sm text-gray-400">
                              {service.quantity_used}/{service.quantity_allowed} {t('membershipCenter.usedThisPeriod', { period: service.period_type })}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-gray-200 text-gray-600">{t('membershipCenter.depleted')}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t('membershipCenter.membershipBenefits')}</h3>
            <Badge variant="outline">{unlockedBenefits.length} {t('common.active')}</Badge>
          </div>

          {/* Unlocked Benefits */}
          {unlockedBenefits.length > 0 && (
            <div className="space-y-3">
              {unlockedBenefits.map((benefit) => (
                <Card key={benefit.id} className="border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Unlock className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{benefit.name}</h4>
                            <Badge className="bg-green-100 text-green-800">{t('membershipCenter.active')}</Badge>
                          </div>
                          {benefit.description && (
                            <p className="text-sm text-gray-500 mt-1">{benefit.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {benefit.value && (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                {benefit.benefit_type === 'discount_percentage' ? `${benefit.value}% off` : 
                                 benefit.benefit_type === 'discount_fixed' ? `$${benefit.value} off` : 
                                 benefit.benefit_type}
                              </Badge>
                            )}
                            {benefit.uses_remaining !== null && (
                              <span className="text-sm text-gray-500">
                                {benefit.uses_remaining} uses remaining
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => handleShowValidationCode(benefit)}
                        className="text-green-600 border-green-300"
                      >
                        <QrCode className="h-4 w-4 mr-2" />
                        {t('membershipCenter.getCode')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Locked Benefits */}
          {lockedBenefits.length > 0 && (
            <>
              <h4 className="text-md font-medium text-gray-600 mt-6 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                {t('membershipCenter.unlockWithSeniority')}
              </h4>
              <div className="space-y-2">
                {lockedBenefits.map((benefit) => (
                  <Card key={benefit.id} className="bg-gray-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Lock className="h-5 w-5 text-gray-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-600">{benefit.name}</h4>
                            <p className="text-sm text-gray-400">
                              {t('membershipCenter.unlocksAt', { months: benefit.unlock_at_months })}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-gray-500">
                          {t('membershipCenter.monthsToGo', { months: benefit.months_until_unlock })}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <h3 className="text-lg font-semibold">{t('membershipCenter.serviceHistory')}</h3>
          
          {service_history.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('membershipCenter.noServiceHistoryYet')}</p>
                <p className="text-sm">{t('membershipCenter.completedServicesAppear')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {service_history.map((record) => (
                <Card key={record.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{record.service_name}</h4>
                          <p className="text-sm text-gray-500">
                            {new Date(record.usage_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">{t('appointments.completed')}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Validation Code Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('membershipCenter.validationCode')}</DialogTitle>
            <DialogDescription>
              {t('membershipCenter.showCodeToStaff')}
            </DialogDescription>
          </DialogHeader>
          {validationCode && (
            <div className="space-y-4">
              <div className="p-6 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500 mb-2">{validationCode.benefit_name}</p>
                <div className="text-3xl font-mono font-bold tracking-wider text-teal-600">
                  {validationCode.validation_code}
                </div>
              </div>
              <Button onClick={copyValidationCode} variant="outline" className="w-full">
                <Copy className="h-4 w-4 mr-2" />
                {t('membershipCenter.copyCode')}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                {validationCode.instructions}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembershipCenter;
